import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import useSWR from 'swr';
import { ProjectorShimmer, Snackbar } from 'Components';
import { AppContext } from 'Context';
import { endpoints } from 'Api';
import { isNewUIEnabled } from 'hooks/useNewUI';
import { ViewerPage } from '../components/page';
import GroupWizardModal from './components/GroupWizardModal';
import DeleteGroupModal from './components/DeleteGroupModal';
import { GroupsClassic } from './GroupsClassic';
import styles from './index.module.css';

// Lucide icons (MIT) inlined for the row actions / stat chips.
const lucideProps = (size = 18) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
});
const IconVideo = ({ size }) => (
  <svg {...lucideProps(size)}>
    <path d="m22 8-6 4 6 4V8Z" />
    <rect width="14" height="12" x="2" y="6" rx="2" ry="2" />
  </svg>
);
const IconPencil = ({ size = 19 }) => (
  <svg {...lucideProps(size)} className={styles.pencilSvg}>
    <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    <path d="m15 5 4 4" />
  </svg>
);
const IconTrash = ({ size = 19 }) => (
  <svg {...lucideProps(size)}>
    <path d="M3 6h18" className={styles.trashLid} />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" className={styles.trashLid} />
    <line x1="10" x2="10" y1="11" y2="17" />
    <line x1="14" x2="14" y1="11" y2="17" />
  </svg>
);

// "3 members" / "1 album" — small count + label helper for the group cards.
const countLabel = (n, word) => {
  const value = n ?? 0;
  return `${value} ${value === 1 ? word : `${word}s`}`;
};

const defaultSnackbar = {
  isVisible: false,
  type: undefined,
  message: undefined,
};

const GroupsNew = () => {
  // eslint-disable-next-line no-script-url
  window.location.href = 'javascript: $zopim.livechat.button.hide()';
  const { langText } = useContext(AppContext);
  const [snackbar, setSnackbar] = useState(defaultSnackbar);
  const [groups, setGroups] = useState(undefined);
  const [openConfirmationModal, setConfirmationModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleted, setDeleting] = useState(false);
  // Group info, members and content all live in one 3-step modal, used for
  // creating ({ mode: 'create' }) and editing ({ mode: 'edit', group }) alike.
  const [wizard, setWizard] = useState(null);
  const groupsText = langText?.viewersSideBar?.groups;
  const commonText = langText?.common;

  const { isValidating: loadingGroups, mutate: fetchGroups } = useSWR(
    endpoints.getMyGroups,
    {
      onSuccess: ({ success, data }) => {
        setGroups(success && data ? data : []);
      },
      onError: () => {
        setGroups([]);
      },
    }
  );

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // ConfirmationModal owns the delete request and flips this flag on success.
  // It closes itself in the same batch, so the target is already gone by the
  // time this runs — refetch rather than trying to splice the row out.
  useEffect(() => {
    if (!isDeleted) return;
    setDeleting(false);
    fetchGroups();
  }, [isDeleted, fetchGroups]);

  const openCreateWizard = useCallback(() => {
    setWizard({ mode: 'create', group: null });
  }, []);

  const openEditWizard = useCallback((group) => {
    setWizard({ mode: 'edit', group });
  }, []);

  return (
    <>
      <ViewerPage
        title={groupsText?.title || 'Groups'}
        addActionName={groupsText?.newGroup || 'New Group'}
        onAddAction={openCreateWizard}
      >
        <div className={styles.allGroupContainer}>
          {loadingGroups && !groups ? (
            <ProjectorShimmer isLight />
          ) : (
            groups?.map(({
              id, title, image, members, members_count, videos_count, albums_count,
            }) => (
              <div key={id} className={styles.groupContainer}>
                <div className={styles.groupHeader} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  {/* Group name */}
                  <div className={styles.groupHeaderLeft} style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <div className={styles.groupAvatar}>
                      {image ? (
                        <img className={styles.groupAvatarImg} src={image} alt={title} />
                      ) : (
                        title ? title.charAt(0).toUpperCase() : 'G'
                      )}
                    </div>
                    <div className={styles.groupTitleWrap}>
                      <span className={styles.groupTitle}>{title}</span>
                      <span style={{ fontSize: 12, color: '#8a8f98', marginTop: 2 }}>
                        {countLabel(members_count ?? (Array.isArray(members) ? members.length : 0), 'member')}
                      </span>
                    </div>
                  </div>
                  {/* Members — stacked avatars (max 5) */}
                  <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                    {Array.isArray(members) && members.length > 0 ? (
                      <div className={styles.memberAvatarStack}>
                        {members.slice(0, 5).map((m, i) => (
                          m?.image ? (
                            <img
                              key={`av-${id}-${i}`}
                              className={styles.memberAvatarImg}
                              src={m.image}
                              alt={m.firstname || m.email || 'Member'}
                              title={m.email}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <span
                              key={`av-${id}-${i}`}
                              className={styles.memberAvatarInitial}
                              title={m?.email}
                            >
                              {(m?.firstname || m?.email || '?').charAt(0).toUpperCase()}
                            </span>
                          )
                        ))}
                        {(members_count ?? members.length) > 5 && (
                          <span className={styles.memberAvatarMore}>
                            {`+${(members_count ?? members.length) - 5}`}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: '#8a8f98' }}>No members</span>
                    )}
                  </div>
                  {/* Contents shared */}
                  <div style={{ flex: '0 0 auto', minWidth: 96, textAlign: 'right' }}>
                    <span className={styles.groupStatChip} title="Content shared with this group">
                      <IconVideo size={13} />
                      {`${(Number(videos_count) || 0) + (Number(albums_count) || 0)} shared`}
                    </span>
                  </div>
                  {/* Actions — delete + edit (opens the 3-step group modal) */}
                  <div className={styles.groupHeaderActions} style={{ flex: '0 0 auto' }}>
                    <span
                      className={styles.iconButtonDelete}
                      onClick={() => {
                        setDeleteTarget({ id, name: title });
                        setConfirmationModal(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setDeleteTarget({ id, name: title });
                          setConfirmationModal(true);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      title="Delete group"
                    >
                      <IconTrash />
                    </span>
                    <span
                      className={styles.iconButton}
                      onClick={() => openEditWizard({ id, title, image })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') openEditWizard({ id, title, image });
                      }}
                      role="button"
                      tabIndex={0}
                      title="Edit group"
                    >
                      <IconPencil />
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </ViewerPage>

      <Snackbar
        isVisible={snackbar?.isVisible}
        message={snackbar?.message || '.....'}
        onClose={() => {
          setSnackbar(defaultSnackbar);
        }}
        type={snackbar?.type}
      />

      <DeleteGroupModal
        open={openConfirmationModal}
        group={deleteTarget}
        onClose={() => {
          setConfirmationModal(false);
          setDeleteTarget(null);
        }}
        cancelLabel={commonText?.goBack || 'Go Back'}
        confirmLabel={commonText?.remove || 'Remove'}
        setSnackbar={setSnackbar}
        setDeleting={setDeleting}
      />

      <GroupWizardModal
        open={Boolean(wizard)}
        mode={wizard?.mode}
        group={wizard?.group}
        onClose={() => setWizard(null)}
        onSaved={fetchGroups}
      />
    </>
  );
};

export const Groups = (props) => (
  isNewUIEnabled() ? <GroupsNew {...props} /> : <GroupsClassic {...props} />
);
